/**
 * main — boot: build the sprite atlas, wire every module together,
 * run the requestAnimationFrame loop.
 */
import EventBus from './core/EventBus.js';
import Economy from './game/Economy.js';
import RNG from './game/RNG.js';
import Game from './game/Game.js';
import { DIFFICULTIES } from './game/Difficulty.js';
import { buildSprites } from './render/Sprites.js';
import ChickenActor from './render/Chicken.js';
import Traffic from './render/Traffic.js';
import Scene from './render/Scene.js';
import Controls from './ui/Controls.js';
import Sound from './audio/Sound.js';

const bus = new EventBus();
const economy = new Economy(bus);
const rng = new RNG();
const game = new Game({ bus, rng, economy });

const sprites = buildSprites();
const chicken = new ChickenActor(sprites);
const traffic = new Traffic(sprites, bus);
const scene = new Scene({
  canvas: document.getElementById('scene'),
  sprites, game, bus, chicken, traffic,
});
new Controls({ bus, game, economy, coinIcon: sprites.coin.toDataURL() });
new Sound(bus);

// dev/testing handle (used by the console checks; harmless in production)
window.__cr = { game, economy, rng, bus, DIFFICULTIES, scene, traffic, chicken };

// ?gallery — static sprite sheet at high zoom, for art iteration
if (location.search.includes('gallery')) {
  document.getElementById('topbar').style.display = 'none';
  document.getElementById('controls').style.display = 'none';
  const cv = document.getElementById('scene');
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = window.innerWidth * dpr;
  cv.height = window.innerHeight * dpr;
  const ctx = cv.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#9aa0ab';
  ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
  const put = (spr, x, y, s) =>
    ctx.drawImage(spr, x, y, spr.logicalW * s, spr.logicalH * s);
  put(sprites.chicken, 20, 30, 3);
  put(sprites.chickenBlink, 480, 30, 3);
  put(sprites.chickenHop, 940, 30, 3);
  put(sprites.chickenDead, 20, 500, 2.2);
  put(sprites.coin, 450, 510, 1.8);
  put(sprites.manhole, 650, 500, 1.8);
  put(sprites.barrier, 880, 520, 1.6);
  let x = 20;
  for (const car of sprites.cars) { put(car, (x += 130), 690, 1.1); }
  put(sprites.fireTruck, (x += 150), 660, 1.0);
}

// Fixed-substep loop: rAF while visible, timer fallback while hidden
// (hidden tabs get no rAF at all — the game must not freeze mid-round).
// Substeps also let the simulation catch up smoothly after throttling.
let last = performance.now();
function tick() {
  const now = performance.now();
  let elapsed = Math.min(1.2, (now - last) / 1000);
  last = now;
  while (elapsed > 0) {
    const dt = Math.min(0.05, elapsed);
    scene.update(dt);
    elapsed -= dt;
  }
  scene.draw();
}
function loop() {
  tick();
  if (document.visibilityState === 'hidden') setTimeout(loop, 33);
  else requestAnimationFrame(loop);
}
if (!location.search.includes('gallery')) loop();
